import React, { useState, useEffect } from 'react';
import { CreditCard, Check, Star, Building2, ArrowRight, Loader2, ExternalLink, AlertCircle, Clock, ShieldCheck, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const PLN = (n) => Number(n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function StatusBadge({ status }) {
  const map = {
    active: { label: 'Aktywna', className: 'bg-green-100 text-green-700', icon: <ShieldCheck size={14} /> },
    trialing: { label: 'Okres próbny', className: 'bg-blue-100 text-blue-700', icon: <Clock size={14} /> },
    past_due: { label: 'Zaległa płatność', className: 'bg-amber-100 text-amber-700', icon: <AlertCircle size={14} /> },
    canceled: { label: 'Anulowana', className: 'bg-red-100 text-red-700', icon: <AlertCircle size={14} /> },
    incomplete: { label: 'Niekompletna', className: 'bg-gray-100 text-gray-600', icon: <AlertCircle size={14} /> },
    unpaid: { label: 'Nieopłacona', className: 'bg-red-100 text-red-700', icon: <AlertCircle size={14} /> },
  };
  const { label, className, icon } = map[status] || { label: status, className: 'bg-gray-100 text-gray-600', icon: null };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {icon}{label}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Plan Card
// ──────────────────────────────────────────────────────────────────────────────

function PlanCard({ plan, billingPeriod, current, isAdmin, onSelect, loading }) {
  const price = billingPeriod === 'yearly' ? plan.yearly_price_pln : plan.monthly_price_pln;
  const isCurrent = current?.plan_key === plan.key && ['active', 'trialing'].includes(current?.status);

  return (
    <div className={`relative bg-white rounded-2xl border-2 transition-all flex flex-col ${
      isCurrent ? 'border-purple-500 shadow-lg shadow-purple-100' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
    }`}>
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">Twój plan</span>
        </div>
      )}

      <div className="p-6 flex-1">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: plan.color + '22' }}>
            <Star size={16} style={{ color: plan.color }} />
          </div>
          <h3 className="text-lg font-bold text-gray-800">{plan.name}</h3>
        </div>

        {/* Price */}
        <div className="mb-4">
          {Number(price) > 0 ? (
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-extrabold text-gray-900">{PLN(price)}</span>
              <span className="text-gray-500 text-sm">PLN / {billingPeriod === 'yearly' ? 'rok' : 'mies.'}</span>
            </div>
          ) : (
            <div className="text-2xl font-extrabold text-gray-900">Bezpłatny</div>
          )}
          {billingPeriod === 'yearly' && Number(plan.monthly_price_pln) > 0 && (
            <div className="text-xs text-green-600 font-medium mt-0.5">
              Oszczędzasz {PLN(Number(plan.monthly_price_pln) * 12 - Number(plan.yearly_price_pln))} PLN rocznie
            </div>
          )}
        </div>

        {/* Description */}
        {plan.description && (
          <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
        )}

        {/* Features */}
        {plan.features && plan.features.length > 0 && (
          <ul className="space-y-2">
            {plan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <Check size={15} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CTA */}
      <div className="p-4 pt-0">
        {isCurrent ? (
          <div className="w-full py-2 text-center text-sm font-medium text-purple-600 bg-purple-50 rounded-lg">
            Aktualny plan
          </div>
        ) : isAdmin ? (
          <button
            onClick={() => onSelect(plan.key)}
            disabled={loading === plan.key}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition"
            style={{ backgroundColor: plan.color, color: '#fff' }}
          >
            {loading === plan.key ? (
              <><Loader2 size={15} className="animate-spin" /> Przetwarzanie...</>
            ) : (
              <>Wybierz plan <ArrowRight size={15} /></>
            )}
          </button>
        ) : (
          <div className="w-full py-2 text-center text-xs text-gray-400">Tylko administrator może zmienić plan</div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Billing details form
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState({ nip: '', company_name_for_invoice: '', address_for_invoice: '' });
  const [savingDetails, setSavingDetails] = useState(false);

  useEffect(() => {
    loadData();

    // Handle return from Stripe Checkout
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === '1') {
      setError(null);
      // Reload subscription after a short delay
      setTimeout(() => loadData(), 2000);
    } else if (params.get('canceled') === '1') {
      setError('Płatność została anulowana. Możesz spróbować ponownie.');
    }
    // Clean URL
    if (params.get('success') || params.get('canceled')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function loadData() {
    try {
      const [plansRes, subRes] = await Promise.allSettled([
        api.get('/billing/plans'),
        api.get('/billing/subscription'),
      ]);
      if (plansRes.status === 'fulfilled') setPlans(plansRes.value.data.plans || []);
      if (subRes.status === 'fulfilled') {
        const sub = subRes.value.data.subscription;
        setSubscription(sub);
        if (sub) {
          setDetails({
            nip: sub.nip || '',
            company_name_for_invoice: sub.company_name_for_invoice || '',
            address_for_invoice: sub.address_for_invoice || '',
          });
        }
      }
    } catch (e) {
      console.error('loadData error', e);
    } finally {
      setInitialLoading(false);
    }
  }

  async function handleSelectPlan(planKey) {
    setError(null);
    setLoading(planKey);
    try {
      const res = await api.post('/billing/create-checkout-session', {
        plan_key: planKey,
        billing_period: billingPeriod,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'Błąd podczas tworzenia sesji płatności';
      setError(msg);
      setLoading(null);
    }
  }

  async function handleCustomerPortal() {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await api.post('/billing/customer-portal');
      if (res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Błąd otwierania portalu klienta');
    } finally {
      setPortalLoading(false);
    }
  }

  async function handleSaveDetails(e) {
    e.preventDefault();
    setSavingDetails(true);
    try {
      await api.put('/billing/details', details);
      setShowDetails(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Błąd zapisu danych');
    } finally {
      setSavingDetails(false);
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    );
  }

  const hasStripeSubscription = subscription?.stripe_subscription_id;
  const trialEndDate = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const periodEndDate = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  const isTrialing = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
          <CreditCard size={20} className="text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Płatności i subskrypcja</h1>
          <p className="text-sm text-gray-500">Zarządzaj planem i fakturami dla Twojej firmy</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Current Subscription Status */}
      {subscription && (
        <div className="mb-8 bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-sm font-semibold text-gray-700">Aktualna subskrypcja</h2>
                <StatusBadge status={subscription.status} />
              </div>
              <div className="text-lg font-bold text-gray-900">{subscription.plan_name || subscription.plan_key}</div>
              {isTrialing && trialEndDate && (
                <p className="text-sm text-blue-600 mt-1">
                  Okres próbny kończy się: <strong>{trialEndDate.toLocaleDateString('pl-PL')}</strong>
                </p>
              )}
              {periodEndDate && !isTrialing && (
                <p className="text-sm text-gray-500 mt-1">
                  {subscription.cancel_at_period_end ? 'Anulowana po' : 'Następne odnowienie'}:{' '}
                  <strong>{periodEndDate.toLocaleDateString('pl-PL')}</strong>
                </p>
              )}
              {isPastDue && subscription.grace_period_ends_at && (
                <p className="text-sm text-amber-600 mt-1">
                  Okres karencji do: <strong>{new Date(subscription.grace_period_ends_at).toLocaleDateString('pl-PL')}</strong>
                </p>
              )}
            </div>

            {isAdmin && hasStripeSubscription && (
              <button
                onClick={handleCustomerPortal}
                disabled={portalLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
              >
                {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                Zarządzaj subskrypcją
              </button>
            )}
          </div>
        </div>
      )}

      {/* Billing Period Toggle */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-lg font-bold text-gray-800">Wybierz plan</h2>
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${billingPeriod === 'monthly' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}
          >
            Miesięcznie
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${billingPeriod === 'yearly' ? 'bg-white shadow text-gray-800' : 'text-gray-500'}`}
          >
            Rocznie <span className="text-green-600 text-xs font-bold">−17%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      {plans.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {plans.map(plan => (
            <PlanCard
              key={plan.key}
              plan={plan}
              billingPeriod={billingPeriod}
              current={subscription}
              isAdmin={isAdmin}
              onSelect={handleSelectPlan}
              loading={loading}
            />
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-400 py-10">Ładowanie planów...</div>
      )}

      {/* Billing Details Section */}
      {isAdmin && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-gray-500" />
              <h3 className="font-semibold text-gray-800">Dane do faktury</h3>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              {showDetails ? 'Ukryj' : 'Edytuj'}
            </button>
          </div>

          {!showDetails && subscription?.nip && (
            <div className="text-sm text-gray-600 space-y-1">
              <div><span className="text-gray-400">NIP:</span> {subscription.nip}</div>
              {subscription.company_name_for_invoice && <div>{subscription.company_name_for_invoice}</div>}
              {subscription.address_for_invoice && <div className="text-gray-500">{subscription.address_for_invoice}</div>}
            </div>
          )}

          {!showDetails && !subscription?.nip && (
            <p className="text-sm text-gray-400">Brak danych do faktury. Kliknij Edytuj, aby uzupełnić NIP i adres.</p>
          )}

          {showDetails && (
            <form onSubmit={handleSaveDetails} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">NIP</label>
                <input
                  type="text"
                  value={details.nip}
                  onChange={e => setDetails(d => ({ ...d, nip: e.target.value }))}
                  placeholder="np. 123-456-78-90"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nazwa firmy na fakturze</label>
                <input
                  type="text"
                  value={details.company_name_for_invoice}
                  onChange={e => setDetails(d => ({ ...d, company_name_for_invoice: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Adres</label>
                <textarea
                  rows={2}
                  value={details.address_for_invoice}
                  onChange={e => setDetails(d => ({ ...d, address_for_invoice: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400 outline-none resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={savingDetails}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
                >
                  {savingDetails ? <Loader2 size={14} className="animate-spin" /> : null}
                  Zapisz
                </button>
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200 transition"
                >
                  Anuluj
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Footer note */}
      <div className="text-xs text-gray-400 text-center space-y-1">
        <p>Płatności obsługiwane przez <strong>Stripe</strong> — bezpieczna i szyfrowana transmisja danych.</p>
        <p>Faktury VAT generowane automatycznie. Okres próbny 14 dni bez karty kredytowej.</p>
        <p>Masz pytania? Napisz do nas: <a href="mailto:kontakt@swibi.pl" className="text-purple-600 hover:underline">kontakt@swibi.pl</a></p>
      </div>
    </div>
  );
}
